import { useState } from "react";
import Header from "@/components/bionexus/Header";
import Footer from "@/components/Footer";
import BackNav from "@/components/BackNav";
import { AlertTriangle, CheckCircle, Plus, Trash2 } from "lucide-react";

// Validation state type
type ValidationState = {
  isValid: boolean;
  warnings: string[];
  errors: string[];
};

const AdminMaterialEditor = () => {
  // Basic info
  const [nameEn, setNameEn] = useState("");
  const [family, setFamily] = useState("");
  const [description, setDescription] = useState("");

  // Paper source (required)
  const [sourcePaper, setSourcePaper] = useState("");
  const [sourceDoi, setSourceDoi] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [paperAbstract, setPaperAbstract] = useState("");

  // Measured data with sources
  const [degradationValue, setDegradationValue] = useState("");
  const [degradationSource, setDegradationSource] = useState("");
  const [degradationMethod, setDegradationMethod] = useState("");

  const [tensileValue, setTensileValue] = useState("");
  const [tensileSource, setTensileSource] = useState("");

  const [electricalPerformance, setElectricalPerformance] = useState("");
  const [electricalSource, setElectricalSource] = useState("");

  const [waterResistance, setWaterResistance] = useState("");
  const [waterResistanceSource, setWaterResistanceSource] = useState("");

  const [costValue, setCostValue] = useState("");
  const [costSource, setCostSource] = useState("");

  // Applications
  const [applications, setApplications] = useState<string[]>([]);
  const [newApplication, setNewApplication] = useState("");
  const [applicationsSource, setApplicationsSource] = useState("");

  // Fabrication
  const [fabricationMethod, setFabricationMethod] = useState("");
  const [fabricationSource, setFabricationSource] = useState("");

  // Image
  const [imageUrl, setImageUrl] = useState("");
  const [imageCredit, setImageCredit] = useState("");
  const [imageSourceUrl, setImageSourceUrl] = useState("");

  // Validate form
  const validateForm = (): ValidationState => {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Required fields
    if (!nameEn.trim()) errors.push("Material name is required");
    if (!family.trim()) errors.push("Material family is required");
    if (!sourcePaper.trim()) errors.push("Paper citation is required");
    if (!sourceDoi.trim()) errors.push("DOI is required");

    // DOI format validation
    if (sourceDoi && !sourceDoi.match(/^10\.\d{4,}\/[^\s]+$/)) {
      errors.push("Invalid DOI format (expected: 10.xxxx/xxxxx)");
    }

    // Data-source pairing validation
    if (degradationValue && !degradationSource) {
      errors.push("Degradation data requires a source reference");
    }
    if (tensileValue && !tensileSource) {
      errors.push("Tensile strength requires a source reference");
    }
    if (electricalPerformance && !electricalSource) {
      errors.push("Electrical performance requires a source reference");
    }
    if (waterResistance && !waterResistanceSource) {
      errors.push("Water resistance requires a source reference");
    }
    if (costValue && !costSource) {
      warnings.push("Cost data has no source - please note if not peer-reviewed");
    }

    // Count measured properties
    const measuredCount = [
      degradationValue,
      tensileValue,
      electricalPerformance,
      waterResistance,
      costValue
    ].filter(Boolean).length;

    if (measuredCount < 2) {
      warnings.push(`Only ${measuredCount} measured properties (consider adding more)`);
    }

    if (!paperAbstract) {
      warnings.push("Consider adding paper abstract");
    }

    if (!description) {
      warnings.push("Consider adding material description");
    }

    return {
      isValid: errors.length === 0,
      warnings,
      errors
    };
  };

  const validation = validateForm();

  const addApplication = () => {
    if (newApplication.trim()) {
      setApplications([...applications, newApplication.trim()]);
      setNewApplication("");
    }
  };

  const removeApplication = (index: number) => {
    setApplications(applications.filter((_, i) => i !== index));
  };

  const handleSubmit = (publish: boolean) => {
    if (!validation.isValid) {
      alert("Please fix errors before submitting");
      return;
    }
    
    // TODO: Submit to Supabase - implementation pending
    // Material data is prepared but not logged for security
    const _materialData = {
      name: nameEn,
      family,
      description,
      source_paper: sourcePaper,
      source_doi: sourceDoi,
      source_url: sourceUrl,
      paper_abstract: paperAbstract,
      degradation_value: degradationValue || null,
      degradation_source: degradationSource || null,
      degradation_method: degradationMethod || null,
      tensile_strength_mpa: tensileValue ? parseFloat(tensileValue) : null,
      tensile_source: tensileSource || null,
      electrical_performance: electricalPerformance || null,
      electrical_source: electricalSource || null,
      water_resistance: waterResistance || null,
      water_resistance_source: waterResistanceSource || null,
      cost_per_kg: costValue ? parseFloat(costValue) : null,
      cost_source: costSource || null,
      applications_mentioned: applications.length > 0 ? applications : null,
      applications_source: applicationsSource || null,
      fabrication_method: fabricationMethod || null,
      fabrication_source: fabricationSource || null,
      image_url: imageUrl || null,
      image_credit: imageCredit || null,
      image_source_url: imageSourceUrl || null,
      published: publish
    };
    
    // Placeholder for actual submission
    alert(publish ? "Publishing is not yet implemented" : "Draft save is not yet implemented");
  };

  return (
    <div className="min-h-screen bg-background page-enter">
      <Header />
      
      <main className="pt-32 pb-20 px-6 lg:px-12">
        <div className="max-w-[900px] mx-auto">
          <div className="mb-8">
            <BackNav to="/admin" label="Back to Admin" />
          </div>

          <h1 className="font-display font-medium text-4xl mb-2">Add Material</h1>
          <p className="font-mono text-xs uppercase text-muted-foreground mb-12">
            Academic integrity: Only enter data measured in published papers
          </p>

          <div className="space-y-12">
            {/* Basic Information */}
            <section className="border border-border p-6">
              <h2 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6">
                Basic Information
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block font-mono text-xs uppercase text-muted-foreground mb-2">
                    Material Name *
                  </label>
                  <input
                    type="text"
                    value={nameEn}
                    onChange={(e) => setNameEn(e.target.value)}
                    className="w-full bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-primary"
                    placeholder="Ethyl Cellulose (E462)"
                  />
                </div>

                <div>
                  <label className="block font-mono text-xs uppercase text-muted-foreground mb-2">
                    Material Family *
                  </label>
                  <select
                    value={family}
                    onChange={(e) => setFamily(e.target.value)}
                    className="w-full bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-primary"
                  >
                    <option value="">Select family...</option>
                    <option value="Cellulose Derivatives">Cellulose Derivatives</option>
                    <option value="Protein-Based">Protein-Based</option>
                    <option value="Polysaccharides">Polysaccharides</option>
                    <option value="Lipid-Based">Lipid-Based</option>
                    <option value="Composite">Composite</option>
                  </select>
                </div>

                <div>
                  <label className="block font-mono text-xs uppercase text-muted-foreground mb-2">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-primary h-32"
                    placeholder="Detailed description of the material, its properties, potential applications, and significance in biodegradable electronics..."
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Provide a comprehensive overview. This will be the main content on the detail page.
                  </p>
                </div>

                <div>
                  <label className="block font-mono text-xs uppercase text-muted-foreground mb-2">
                    Image URL
                  </label>
                  <input
                    type="url"
                    value={imageUrl}
                    onChange={(e) => {
                      setImageUrl(e.target.value);
                      setImageCredit("");
                      setImageSourceUrl("");
                    }}
                    className="w-full bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-primary"
                    placeholder="https://..."
                  />
                </div>

                <div>
                  <label className="block font-mono text-xs uppercase text-muted-foreground mb-2">
                    Image Credit
                  </label>
                  <input
                    type="text"
                    value={imageCredit}
                    onChange={(e) => setImageCredit(e.target.value)}
                    className="w-full bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-primary"
                    placeholder="Creator, photographer, or publication"
                  />
                </div>

                <div>
                  <label className="block font-mono text-xs uppercase text-muted-foreground mb-2">
                    Image Source URL
                  </label>
                  <input
                    type="url"
                    value={imageSourceUrl}
                    onChange={(e) => setImageSourceUrl(e.target.value)}
                    className="w-full bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-primary"
                    placeholder="https://official-project-or-publication-page..."
                  />
                </div>
              </div>
            </section>

            {/* Paper Source - REQUIRED */}
            <section className="border border-primary p-6">
              <h2 className="font-mono text-xs uppercase tracking-widest text-primary mb-6 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Research Source (Required)
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block font-mono text-xs uppercase text-muted-foreground mb-2">
                    Paper Citation * (Full Format)
                  </label>
                  <input
                    type="text"
                    value={sourcePaper}
                    onChange={(e) => setSourcePaper(e.target.value)}
                    className="w-full bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-primary"
                    placeholder="Palmieri et al., RSC Advances, 2024, 14, xxxx"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Example: "Author et al., Journal Name, Year, Volume, Page"
                  </p>
                </div>

                <div>
                  <label className="block font-mono text-xs uppercase text-muted-foreground mb-2">
                    DOI *
                  </label>
                  <input
                    type="text"
                    value={sourceDoi}
                    onChange={(e) => setSourceDoi(e.target.value)}
                    className="w-full bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-primary"
                    placeholder="10.1039/d4ra02993c"
                  />
                </div>

                <div>
                  <label className="block font-mono text-xs uppercase text-muted-foreground mb-2">
                    Paper URL
                  </label>
                  <input
                    type="url"
                    value={sourceUrl}
                    onChange={(e) => setSourceUrl(e.target.value)}
                    className="w-full bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-primary"
                    placeholder="https://pubs.rsc.org/..."
                  />
                </div>

                <div>
                  <label className="block font-mono text-xs uppercase text-muted-foreground mb-2">
                    Paper Abstract (Copy from paper)
                  </label>
                  <textarea
                    value={paperAbstract}
                    onChange={(e) => setPaperAbstract(e.target.value)}
                    className="w-full bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-primary h-32"
                    placeholder="Abstract from paper..."
                  />
                </div>
              </div>
            </section>

            {/* Measured Data */}
            <section className="border border-border p-6">
              <h2 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
                Measured Properties
              </h2>
              <p className="text-xs text-muted-foreground mb-6">
                Only enter data that was actually measured and reported in the paper. Leave blank if not measured.
              </p>
              
              <div className="space-y-8">
                {/* Degradation */}
                <div className="border-b border-border pb-6">
                  <h3 className="font-mono text-sm mb-4">Biodegradation</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block font-mono text-xs text-muted-foreground mb-2">Value</label>
                      <input
                        type="text"
                        value={degradationValue}
                        onChange={(e) => setDegradationValue(e.target.value)}
                        className="w-full bg-transparent border border-border px-3 py-2 font-mono text-sm focus:outline-none focus:border-primary"
                        placeholder="3-10 months"
                      />
                    </div>
                    <div>
                      <label className="block font-mono text-xs text-muted-foreground mb-2">Source Reference</label>
                      <input
                        type="text"
                        value={degradationSource}
                        onChange={(e) => setDegradationSource(e.target.value)}
                        className="w-full bg-transparent border border-border px-3 py-2 font-mono text-sm focus:outline-none focus:border-primary"
                        placeholder="Figure 3, Table 2"
                      />
                    </div>
                    <div>
                      <label className="block font-mono text-xs text-muted-foreground mb-2">Test Method</label>
                      <input
                        type="text"
                        value={degradationMethod}
                        onChange={(e) => setDegradationMethod(e.target.value)}
                        className="w-full bg-transparent border border-border px-3 py-2 font-mono text-sm focus:outline-none focus:border-primary"
                        placeholder="Composting conditions"
                      />
                    </div>
                  </div>
                </div>

                {/* Tensile */}
                <div className="border-b border-border pb-6">
                  <h3 className="font-mono text-sm mb-4">Tensile Strength</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-mono text-xs text-muted-foreground mb-2">Value (MPa)</label>
                      <input
                        type="number"
                        value={tensileValue}
                        onChange={(e) => setTensileValue(e.target.value)}
                        className="w-full bg-transparent border border-border px-3 py-2 font-mono text-sm focus:outline-none focus:border-primary"
                        placeholder="850"
                      />
                    </div>
                    <div>
                      <label className="block font-mono text-xs text-muted-foreground mb-2">Source Reference</label>
                      <input
                        type="text"
                        value={tensileSource}
                        onChange={(e) => setTensileSource(e.target.value)}
                        className="w-full bg-transparent border border-border px-3 py-2 font-mono text-sm focus:outline-none focus:border-primary"
                        placeholder="Table 2, UTM test"
                      />
                    </div>
                  </div>
                </div>

                {/* Electrical */}
                <div className="border-b border-border pb-6">
                  <h3 className="font-mono text-sm mb-4">Electrical Performance</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-mono text-xs text-muted-foreground mb-2">Value</label>
                      <input
                        type="text"
                        value={electricalPerformance}
                        onChange={(e) => setElectricalPerformance(e.target.value)}
                        className="w-full bg-transparent border border-border px-3 py-2 font-mono text-sm focus:outline-none focus:border-primary"
                        placeholder="RSD 10%"
                      />
                    </div>
                    <div>
                      <label className="block font-mono text-xs text-muted-foreground mb-2">Source Reference</label>
                      <input
                        type="text"
                        value={electricalSource}
                        onChange={(e) => setElectricalSource(e.target.value)}
                        className="w-full bg-transparent border border-border px-3 py-2 font-mono text-sm focus:outline-none focus:border-primary"
                        placeholder="Figure 5"
                      />
                    </div>
                  </div>
                </div>

                {/* Water Resistance */}
                <div className="border-b border-border pb-6">
                  <h3 className="font-mono text-sm mb-4">Water Resistance</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-mono text-xs text-muted-foreground mb-2">Value</label>
                      <input
                        type="text"
                        value={waterResistance}
                        onChange={(e) => setWaterResistance(e.target.value)}
                        className="w-full bg-transparent border border-border px-3 py-2 font-mono text-sm focus:outline-none focus:border-primary"
                        placeholder="Superior to CNF"
                      />
                    </div>
                    <div>
                      <label className="block font-mono text-xs text-muted-foreground mb-2">Source Reference</label>
                      <input
                        type="text"
                        value={waterResistanceSource}
                        onChange={(e) => setWaterResistanceSource(e.target.value)}
                        className="w-full bg-transparent border border-border px-3 py-2 font-mono text-sm focus:outline-none focus:border-primary"
                        placeholder="Table 1, Section 3.2"
                      />
                    </div>
                  </div>
                </div>

                {/* Cost */}
                <div>
                  <h3 className="font-mono text-sm mb-4">Cost</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-mono text-xs text-muted-foreground mb-2">Value ($/kg)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={costValue}
                        onChange={(e) => setCostValue(e.target.value)}
                        className="w-full bg-transparent border border-border px-3 py-2 font-mono text-sm focus:outline-none focus:border-primary"
                        placeholder="15.00"
                      />
                    </div>
                    <div>
                      <label className="block font-mono text-xs text-muted-foreground mb-2">Source Reference</label>
                      <input
                        type="text"
                        value={costSource}
                        onChange={(e) => setCostSource(e.target.value)}
                        className="w-full bg-transparent border border-border px-3 py-2 font-mono text-sm focus:outline-none focus:border-primary"
                        placeholder="Market survey (note if not peer-reviewed)"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Applications from Paper */}
            <section className="border border-border p-6">
              <h2 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6">
                Applications Mentioned in Paper
              </h2>
              
              <div className="space-y-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newApplication}
                    onChange={(e) => setNewApplication(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addApplication()}
                    className="flex-1 bg-transparent border border-border px-4 py-2 font-mono text-sm focus:outline-none focus:border-primary"
                    placeholder="Screen-printed electrodes"
                  />
                  <button
                    onClick={addApplication}
                    className="px-4 py-2 bg-primary text-primary-foreground font-mono text-sm"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                {applications.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {applications.map((app, i) => (
                      <span key={i} className="flex items-center gap-2 bg-muted px-3 py-1.5 font-mono text-sm">
                        {app}
                        <button onClick={() => removeApplication(i)}>
                          <Trash2 className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div>
                  <label className="block font-mono text-xs text-muted-foreground mb-2">Source Section</label>
                  <input
                    type="text"
                    value={applicationsSource}
                    onChange={(e) => setApplicationsSource(e.target.value)}
                    className="w-full bg-transparent border border-border px-4 py-2 font-mono text-sm focus:outline-none focus:border-primary"
                    placeholder="Discussion section, Results"
                  />
                </div>
              </div>
            </section>

            {/* Fabrication */}
            <section className="border border-border p-6">
              <h2 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6">
                Fabrication Method
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block font-mono text-xs text-muted-foreground mb-2">Method Description</label>
                  <textarea
                    value={fabricationMethod}
                    onChange={(e) => setFabricationMethod(e.target.value)}
                    className="w-full bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-primary h-24"
                    placeholder="Fabrication process from paper..."
                  />
                </div>
                <div>
                  <label className="block font-mono text-xs text-muted-foreground mb-2">Source Reference</label>
                  <input
                    type="text"
                    value={fabricationSource}
                    onChange={(e) => setFabricationSource(e.target.value)}
                    className="w-full bg-transparent border border-border px-4 py-2 font-mono text-sm focus:outline-none focus:border-primary"
                    placeholder="Methods section"
                  />
                </div>
              </div>
            </section>

            {/* Validation Summary */}
            <section className="border border-border p-6">
              <h2 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6">
                Validation
              </h2>
              
              <div className="space-y-4">
                {validation.errors.length > 0 && (
                  <div className="space-y-2">
                    {validation.errors.map((error, i) => (
                      <div key={i} className="flex items-center gap-2 text-destructive text-sm">
                        <AlertTriangle className="w-4 h-4" />
                        {error}
                      </div>
                    ))}
                  </div>
                )}

                {validation.warnings.length > 0 && (
                  <div className="space-y-2">
                    {validation.warnings.map((warning, i) => (
                      <div key={i} className="flex items-center gap-2 text-amber-400 text-sm">
                        <AlertTriangle className="w-4 h-4" />
                        {warning}
                      </div>
                    ))}
                  </div>
                )}

                {validation.isValid && validation.warnings.length === 0 && (
                  <div className="flex items-center gap-2 text-primary text-sm">
                    <CheckCircle className="w-4 h-4" />
                    All required fields complete with proper source references
                  </div>
                )}
              </div>
            </section>

            {/* Submit Buttons */}
            <div className="flex gap-4">
              <button
                onClick={() => handleSubmit(false)}
                className="px-6 py-3 border border-border font-mono text-sm hover:bg-muted transition-colors"
              >
                Save Draft
              </button>
              <button
                onClick={() => handleSubmit(true)}
                disabled={!validation.isValid}
                className="px-6 py-3 bg-primary text-primary-foreground font-mono text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Publish
              </button>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AdminMaterialEditor;